import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, ChevronDown, ChevronUp, Camera, ChevronRight } from 'lucide-react';
import { GuestBottomNav } from '../components/GuestBottomNav';
import NotificationBellButton from '../components/NotificationBellButton';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { hostCardStyle } from '../components/HostSettingsCard';
import { useProfilePhotoUpload } from '../hooks/useProfilePhotoUpload';
import { resolveAvatarUrl } from '../utils/resolveAvatarUrl';
import { useFavoriteEvents } from '../hooks/useFavoriteEvents';
import WelcomeTourSheet from '../components/WelcomeTourSheet';
import PageInfoButton from '../components/PageInfoButton';
import { guestWelcomeTourSlides } from '../components/welcomeTourSlides';
import ThemeToggle from '../components/ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';
import ProfileThemeFlash from '../components/ProfileThemeFlash';
import ProfilePalettePicker from '../components/ProfilePalettePicker';
import LogoutConfirmModal from '../components/LogoutConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// "Starts in 2h 35m" / "Starts in 3d" — short, friendly relative-time label.
function relativeStartLabel(startIso) {
  if (!startIso) return '';
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return '';
  const diffMs = start.getTime() - Date.now();
  if (diffMs <= 0) return 'Starting now';
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 60) return `Starts in ${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const remMin = minutes % 60;
    return remMin ? `Starts in ${hours}h ${remMin}m` : `Starts in ${hours}h`;
  }
  const days = Math.floor(hours / 24);
  return `Starts in ${days}d`;
}

export default function ProfilePage({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { fileInputRef, uploading, openPicker, handleFileChange, user, setUser } = useProfilePhotoUpload();
  const [savedExpanded, setSavedExpanded] = useState(false);
  const [savedEvents, setSavedEvents] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const { favoriteIds, favoriteCount, toggleFavorite } = useFavoriteEvents();
  const { isDark } = useTheme();
  const mutedIcon = isDark ? '#9A9AA5' : '#666';

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    // Hook seeds from localStorage; refresh if storage gets updated elsewhere.
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch { /* ignore */ }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch the saved events whenever the favorite set changes so the list
  // stays in sync (also handles the optimistic remove case below).
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    setSavedLoading(true);
    axios
      .get(`${API}/guest/event-favorites`, { headers })
      .then((res) => {
        if (cancelled) return;
        setSavedEvents(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (cancelled) return;
        setSavedEvents([]);
      })
      .finally(() => {
        if (cancelled) return;
        setSavedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [favoriteIds, headers, token]);

  const displayName = user?.name || user?.username || 'Guest';

  const getVenueImage = (name) => {
    if (name?.includes('Taverna') || name?.includes('Opa')) return 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=100&h=100&fit=crop';
    if (name?.includes('Blue') || name?.includes('Bar')) return 'https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=100&h=100&fit=crop';
    if (name?.includes('Caf') || name?.includes('Nobile')) return 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=100&h=100&fit=crop';
    return 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=100&h=100&fit=crop';
  };

  const requestLogout = () => setConfirmLogout(true);
  const handleLogout = () => {
    setConfirmLogout(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (onLogout) onLogout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="app-shell" style={{ backgroundColor: 'var(--app-bg)' }}>
      <ProfileThemeFlash />
      {/* Bell + ThemeToggle */}
      <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
        <ThemeToggle />
        <NotificationBellButton buttonTestId="guest-profile-bell-button" popupTestId="guest-profile-notification-popup" />
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 0 }}>
        {/* Logo */}
        <RouteTransitionLogo width={132} height={118} dataTestId="guest-profile-shared-logo" style={{ marginBottom: 16 }} />

        {/* Profile card */}
        <div className="profile-name-card" style={{
          ...hostCardStyle,
          position: 'relative',
          width: '100%',
          background: 'var(--profile-card-gradient)',
          borderRadius: 24,
          border: 'none',
          boxShadow: 'none',
          padding: '20px 20px', display: 'flex', alignItems: 'center', gap: 16,
        }}>
          {/* Profile photo with edit overlay */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div
              onClick={openPicker}
              data-testid="guest-profile-photo-target"
              style={{
                width: 80, height: 80, borderRadius: '50%', overflow: 'hidden',
                backgroundColor: 'var(--hairline)', border: '3px solid var(--surface)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                cursor: uploading ? 'wait' : 'pointer',
                opacity: uploading ? 0.7 : 1,
                transition: 'opacity 200ms ease',
              }}
            >
              {resolveAvatarUrl(user?.photo_url) ? (
                <img src={resolveAvatarUrl(user.photo_url)} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)' }}>
                  <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 24, fontWeight: 700, color: 'var(--text-3)' }}>
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            {/* Camera edit button */}
            <button
              onClick={openPicker}
              disabled={uploading}
              aria-label="Change profile photo"
              data-testid="guest-profile-photo-edit-btn"
              style={{
                position: 'absolute', bottom: -2, left: -2,
                width: 26, height: 26, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--surface)', border: '1px solid var(--accent-purple)',
                padding: 0, cursor: uploading ? 'wait' : 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              <Camera size={13} color="var(--accent-purple)" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              data-testid="guest-profile-photo-input"
            />
          </div>

          {/* Name + role */}
          <div>
            <h2 style={{ fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
              {displayName}
            </h2>
            <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)', margin: 0, marginTop: 2 }}>
              Guest traveler
            </p>
          </div>
        </div>

        {/* Palette preview strip — tap a dot to retint the whole app live. */}
        <ProfilePalettePicker testIdPrefix="guest-profile-palette" />

        {/* My list — saved upcoming live events (notifies 2h before) */}
        <div
          data-testid="guest-profile-saved-list"
          style={{
            ...hostCardStyle,
            width: '100%', background: 'var(--surface)', borderRadius: 20,
            marginBottom: 20, overflow: 'hidden',
          }}
        >
          <button
            data-testid="guest-profile-saved-list-toggle"
            onClick={() => setSavedExpanded(!savedExpanded)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              background: 'transparent', border: 'none',
              padding: '14px 20px', cursor: 'pointer',
            }}
          >
            {savedExpanded ? <ChevronUp size={18} color={mutedIcon} /> : <ChevronDown size={18} color={mutedIcon} />}
            <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text-2)', fontWeight: 700 }}>
              My list
            </span>
            <span
              data-testid="guest-profile-saved-count"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px', borderRadius: 999,
                background: 'rgba(255, 201, 60, 0.18)',
                color: '#A06A00',
                fontFamily: "'Inria Serif', serif", fontSize: 12, fontWeight: 700,
                marginLeft: 2,
              }}
            >
              <Bell size={11} fill="#A06A00" color="#A06A00" />
              {savedEvents.length}
            </span>
          </button>

          <AnimatePresence>
            {savedExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                style={{ overflow: 'hidden' }}
              >
                <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 360, overflowY: 'auto' }}>
                  {savedEvents.length === 0 ? (
                    <div
                      data-testid="guest-profile-saved-empty"
                      style={{
                        padding: '18px 12px 12px',
                        textAlign: 'center',
                      }}
                    >
                      <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)', margin: 0, lineHeight: 1.4 }}>
                        Never miss a night. Tap the bell on any live-event card and we'll buzz you 2 hours before it kicks off.
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate('/guest/venues')}
                        data-testid="guest-profile-saved-empty-cta"
                        style={{
                          marginTop: 12,
                          padding: '9px 18px', borderRadius: 999,
                          border: 'none', cursor: 'pointer',
                          background: 'var(--accent-purple)',
                          color: '#FFFFFF',
                          fontFamily: "'Inria Serif', serif", fontSize: 13.5, fontWeight: 700,
                          boxShadow: 'var(--premium-shadow-main)',
                        }}
                      >
                        Browse upcoming events
                      </button>
                    </div>
                  ) : savedLoading && savedEvents.length === 0 ? (
                    <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 13, color: 'var(--text-3)', textAlign: 'center', padding: 12 }}>
                      Loading…
                    </p>
                  ) : (
                    savedEvents.map((event) => {
                      const venue = event.venue || {};
                      const venueImg = venue.main_photo || (venue.photo_urls && venue.photo_urls[0]) || getVenueImage(venue.name);
                      const title = event.display_title || event.title || 'Live event';
                      const startLabel = relativeStartLabel(event.start_at_utc);
                      return (
                        <div
                          key={event.id}
                          data-testid={`guest-profile-saved-event-${event.id}`}
                          style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                        >
                          {/* Tap row → jump to that venue on the map */}
                          <button
                            type="button"
                            data-testid={`guest-profile-saved-open-${event.id}`}
                            onClick={() =>
                              navigate('/guest/venues', {
                                state: { targetVenueId: venue.id },
                              })
                            }
                            style={{
                              flex: 1, minWidth: 0,
                              display: 'flex', alignItems: 'center', gap: 12,
                              background: 'transparent', border: 'none',
                              padding: 0, cursor: 'pointer', textAlign: 'left',
                            }}
                          >
                            {/* Venue photo */}
                            <div style={{ width: 48, height: 48, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, backgroundColor: 'var(--hairline)' }}>
                              <img src={venueImg} alt={venue.name || title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                            {/* Title + venue · time */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{
                                fontFamily: "'Inria Serif', serif", fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>{title}</p>
                              <p style={{
                                fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-2)', margin: 0, marginTop: 1,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                {venue.name || ''}{startLabel ? ` · ${startLabel}` : ''}
                              </p>
                            </div>
                          </button>
                          {/* Bell — tap to remove the reminder */}
                          <button
                            type="button"
                            data-testid={`guest-profile-saved-remove-${event.id}`}
                            aria-label="Remove reminder"
                            onClick={() => toggleFavorite(event.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                          >
                            <Bell size={24} fill="#FFC93C" color="#A06A00" strokeWidth={2} />
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Menu items */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: '100%' }}>
          {['Account Settings', 'Allure Support', 'Switch to..'].map((item) => (
            <button
              key={item}
              onClick={() => {
                if (item === 'Switch to..') navigate('/switch-role', { state: { fromPath: location.pathname } });
                else if (item === 'Allure Support') navigate('/support');
                else navigate('/guest/settings');
              }}
              data-testid={`guest-profile-${item.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              style={{
                ...hostCardStyle,
                width: '100%',
                background: 'var(--surface)', cursor: 'pointer', padding: '14px 16px',
                fontFamily: "'Inria Serif', serif", fontSize: 17, color: 'var(--text)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left',
              }}
            >
              <span>{item}</span>
              <ChevronRight size={18} color={mutedIcon} />
            </button>
          ))}

          {/* Log Out - red */}
          <button
            onClick={requestLogout}
            data-testid="guest-profile-logout-button"
            style={{
              ...hostCardStyle,
              width: '100%',
              background: 'var(--surface)', cursor: 'pointer',
              border: '1px solid var(--accent-purple)',
              padding: '14px 16px', marginTop: 2,
              fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--accent-purple)',
            }}
          >
            Log Out
          </button>
        </div>
      </div>

      <GuestBottomNav />
      <LogoutConfirmModal
        open={confirmLogout}
        onCancel={() => setConfirmLogout(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
}
